import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Hls from "hls.js";

import * as Material from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { Button } from "@fragments";
import { ReactComponent as Camera } from "@draws/camera.svg";

import { useVideoUpload, isUploadAborted } from "../../hooks/useVideoUpload";
import { readVideoMetadata } from "../../utils/readVideoMetadata";

import "./styles.scss";

const VIDEO_TYPES = ["video/mp4", "video/quicktime"];
const DONE_MESSAGE_MS = 1500;

function formatTime(seconds) {
    if (!seconds || !isFinite(seconds)) return "...";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
}

/**
 * Video picker + preview + upload progress. The upload half of an
 * upload-then-save form: `onChange` receives `final_url` only after the PUT
 * returned 2xx, and `onUploadingChange` lets the host keep Save disabled (and
 * ask before closing) while a file is in flight.
 *
 * @param {string|null} value          persisted video URL
 * @param {(url: string|null, metadata?: { duration: number, aspectRatio: number }|null) => void} onChange
 *        the uploaded URL with the file's metadata (null when unreadable), or
 *        null on remove
 * @param {(uploading: boolean) => void} [onUploadingChange]
 * @param {(localUrl: string, file: File) => void} [onFileSelected]  a blob URL
 *        of the picked file, for metadata (duration, ratio) and thumbnails
 * @param {number} [aspectRatio=16/9]  box ratio; below 1 switches to vertical mode
 * @param {string} [emptyTitle]        headline of the empty picker
 */
export default function VideoUploadField({
    value,
    onChange,
    onUploadingChange,
    onFileSelected,
    aspectRatio = 16 / 9,
    emptyTitle,
}) {
    const { t } = useTranslation("content");
    const { upload, cancel, status, isUploading } = useVideoUpload({ onUploadingChange });

    // The picked file plays from memory while it uploads, and keeps doing so
    // afterwards so the preview does not reload from the network.
    const [localUrl, setLocalUrl] = useState(null);
    const [showDone, setShowDone] = useState(false);

    const inputRef = useRef(null);
    const videoRef = useRef(null);
    // Bumped on every pick and remove, so a late upload result for a file the
    // creator already discarded is ignored.
    const selectionRef = useRef(0);

    const shownUrl = localUrl || value;

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !shownUrl) return;

        if (shownUrl.includes(".m3u8") && Hls.isSupported()) {
            const hls = new Hls();
            hls.loadSource(shownUrl);
            hls.attachMedia(video);
            return () => hls.destroy();
        }

        video.src = shownUrl;
    }, [shownUrl]);

    useEffect(() => {
        if (!localUrl) return;
        return () => URL.revokeObjectURL(localUrl);
    }, [localUrl]);

    useEffect(() => {
        if (!showDone) return;
        const timer = setTimeout(() => setShowDone(false), DONE_MESSAGE_MS);
        return () => clearTimeout(timer);
    }, [showDone]);

    async function handleFileChange(event) {
        const file = event.target.files?.[0];
        // Let the same file be picked again after a remove or a failure.
        event.target.value = "";
        if (!file) return;

        if (!VIDEO_TYPES.includes(file.type)) {
            window.setSnackbar?.(t("videoUpload.invalidFormat"));
            return;
        }

        const selection = ++selectionRef.current;
        const blobUrl = URL.createObjectURL(file);
        setLocalUrl(blobUrl);
        setShowDone(false);
        onFileSelected?.(blobUrl, file);

        const metadata = readVideoMetadata(blobUrl);

        try {
            const finalUrl = await upload(file);
            const info = await metadata;
            if (selection !== selectionRef.current) return;
            onChange(finalUrl, info);
            setShowDone(true);
        } catch (error) {
            if (isUploadAborted(error) || selection !== selectionRef.current) return;
            console.error(error);
            window.setSnackbar?.(t("videoUpload.error"));
            // Back to the saved video, if there is one.
            setLocalUrl(null);
        }
    }

    function handleRemove() {
        selectionRef.current++;
        cancel();
        setLocalUrl(null);
        setShowDone(false);
        onChange(null);
    }

    const isVertical = aspectRatio < 1;

    return (
        <div className="video-upload-field">
            <div
                className={
                    "video-upload" +
                    (!shownUrl ? " upload-mode" : "") +
                    (isVertical ? " vertical-mode" : " horizontal-mode")
                }
                style={{ aspectRatio }}
            >
                {isUploading && <Material.LinearProgress />}
                {shownUrl ? (
                    <>
                        <Material.Tooltip title={t("videoUpload.removeTooltip")} arrow>
                            <Material.IconButton
                                className="remove-video"
                                children={<DeleteIcon color="white" />}
                                onClick={handleRemove}
                            />
                        </Material.Tooltip>
                        <div style={{ width: "100%", height: "100%" }}>
                            <video
                                ref={videoRef}
                                controls
                                autoPlay
                                muted
                                width="100%"
                                height="100%"
                                style={{ objectFit: "contain" }}
                            />
                        </div>
                    </>
                ) : (
                    <div className="upload-content" onClick={() => inputRef.current?.click()}>
                        <Camera className="camera-video-upload" />
                        <h5 className="mt-2 bold">{emptyTitle || t("videoUpload.emptyTitle")}</h5>
                        <small style={{ opacity: 0.6 }}>{t("videoUpload.optional")}</small>
                        <Button
                            variant="contained"
                            color="white"
                            endIcon={<AddIcon />}
                            text={t("videoUpload.addButton")}
                            className="mt-1 mb-1"
                        />
                        <small style={{ opacity: 0.6 }}>
                            {t("videoUpload.formatsHint", {
                                orientation: isVertical
                                    ? t("videoUpload.orientationVertical")
                                    : t("videoUpload.orientationHorizontal"),
                            })}
                        </small>
                    </div>
                )}
                <input
                    type="file"
                    ref={inputRef}
                    style={{ display: "none" }}
                    accept="video/mp4,video/quicktime,.mp4,.mov"
                    onChange={handleFileChange}
                />
            </div>

            {status && (
                <div className="video-upload-block mb-1 mt-2">
                    <div className="video-upload-block__header">
                        <small className="bold">{status.name}</small>
                        {status.finishing ? (
                            <small className="video-upload-block__meta">{t("videoUpload.finishing")}</small>
                        ) : status.secondsLeft !== null && (
                            <small className="video-upload-block__meta">
                                {t("videoUpload.timeRemaining", { time: formatTime(status.secondsLeft) })}
                            </small>
                        )}
                    </div>
                    <Material.LinearProgress
                        variant={status.finishing ? "indeterminate" : "determinate"}
                        value={status.percent}
                        className="video-upload-block__bar"
                    />
                </div>
            )}

            {showDone && !status && (
                <small className="video-upload-done mt-1">{t("videoUpload.done")}</small>
            )}
        </div>
    );
}
